import { useMemo, useState } from 'react'
import { getFromLS, saveToLS } from '../../../../utils/localStore/localStore'
import { autoGrain, bucketKeys, compareRange, dayKey, offsetRange, PERIODS, resolveRange } from './period'

const STORE = 'analytics-widget-period'

/*
 * The period one widget shows. It starts at the widget's configured `period`
 * and remembers what the user picks, per widget type, in this browser — two
 * cards on a dashboard can show different periods, and come back showing them.
 *
 * `grain` in config fixes the bucket size; without it the period chooses one.
 * `periods` in config narrows the choice — a table of recent invoices has no
 * use for "last 12 months".
 *
 * `step(−1)` / `step(1)` move to the period before or after (offsetRange). How
 * far the card has stepped is not remembered: it opens on the present again,
 * since a dashboard quietly showing last March is a trap. A custom range is the
 * exception — it is already a set of dates, so a step moves the dates
 * themselves, and they are kept like any other custom range.
 */
/*
 * `periodSelector: false` fixes the widget to its configured `period`: no list,
 * no arrows, and no choice remembered from before — for a figure that is a
 * level now rather than a total over time, such as stock on hand.
 */
const usePeriod = (widgetKey, config = {}) => {
    const selectable = config.periodSelector !== false
    const allowed = config.periods?.length ? config.periods.filter(p => PERIODS.includes(p)) : PERIODS
    const initial = selectable ? getFromLS(STORE, widgetKey) : null
    const [state, setState] = useState(() =>
        initial && allowed.includes(initial.period) ? initial : { period: allowed.includes(config.period) ? config.period : allowed[0] ?? 'last30Days', custom: null })
    const [offset, setOffset] = useState(0)

    const update = (next) => {
        setState(next)
        setOffset(0)
        saveToLS(STORE, widgetKey, next)
    }

    return useMemo(() => {
        const base = resolveRange(state.period, state.custom)
        const range = offsetRange(base, offset)
        const grain = config.grain ?? autoGrain(range)
        const previous = compareRange(range, config.compare ?? 'previous')

        // Forward while the next period has begun — "last month" steps on to
        // this month, "this month" stops, there being nothing yet in November
        const canStepForward = offsetRange(base, offset + 1).from <= new Date()

        const step = (n) => {
            if (n > 0 && !canStepForward) return
            if (state.period !== 'custom') return setOffset(o => o + n)
            const moved = offsetRange(base, n)
            update({ period: 'custom', custom: { from: dayKey(moved.from), to: dayKey(moved.to) } })
        }

        return {
            period: state.period,
            custom: state.custom,
            selectable,
            allowed,
            offset,
            range,
            previous,
            grain,
            keys: bucketKeys(range, grain),
            previousKeys: previous ? bucketKeys(previous, grain) : [],
            canStepForward,
            step,
            setPeriod: (period) => update({ period, custom: period === 'custom' ? state.custom ?? null : null }),
            setCustom: (custom) => update({ period: 'custom', custom }),
        }
    }, [state, offset, selectable, config.grain, config.compare, allowed.join()])
}

export default usePeriod
